import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { site } from "@/config/site";

const values = [
  { icon: "ri-grid-fill", title: "分类清晰", desc: "按穿越、系统、重生、日常、战斗等类型精细整理，定位准确不混淆。" },
  { icon: "ri-file-text-line", title: "简介真实", desc: "作品简介与更新状态如实呈现，不夸大、不误导，帮助读者理性选择。" },
  { icon: "ri-refresh-line", title: "更新及时", desc: "持续跟进作品更新，第一时间呈现在最新列表，追更不迷路。" },
  { icon: "ri-heart-line", title: "尊重创作", desc: "尊重每一位同人创作者的劳动成果，用心推荐值得被看见的作品。" },
];

export default function About() {
  usePageMeta(
    "关于我们 - 火影同人动漫：让每一类同人都被看见",
    `了解火影同人动漫的品牌介绍、网站定位、内容原则与用户口碑。我们致力于按剧情类型整理火影同人作品，帮助读者快速找到对味的作品。`,
  );

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于火影同人动漫",
    description: "火影同人动漫的品牌介绍、网站定位、内容原则与用户口碑。",
    inLanguage: "zh-CN",
    url: "https://chakra321.github.io/about",
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <section className="relative overflow-hidden bg-background-100">
        <div className="container-x py-14 md:py-20">
          <Breadcrumb crumbs={[{ label: "关于我们" }]} />
          <div className="mx-auto mt-10 max-w-3xl text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-fire-fill text-3xl" />
            </span>
            <h1 className="mt-6 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
              关于火影同人动漫
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              {site.slogan}。我们是一处面向火影同人爱好者的作品整理与推荐站点，用清晰的分类，让每一类同人作品都被真正需要它的读者看到。
            </p>
          </div>
        </div>
      </section>

      <section className="container-x py-14 md:py-20">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-start">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-primary-600">
              品牌介绍
            </span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950">
              因为热爱，所以认真整理
            </h2>
            <p className="mt-5 text-sm leading-7 text-foreground-700 md:text-base">
              火影同人动漫诞生于对火影世界同人创作的热忱。火影的世界足够辽阔，同人作品更是百花齐放——从穿越重写的爽快，到系统加身的畅快，从日常的温馨，到战斗的热血，每一种都值得被认真对待。
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">
              我们注意到，同人作品数量庞大、类型混杂，很多读者常常"想看却找不到对味的"。于是我们决定按剧情类型进行系统整理，让分类成为读者寻找作品的快捷方式，帮大家少走弯路。
            </p>

            <h2 className="mt-8 font-heading text-2xl font-black text-foreground-950">网站定位与发展理念</h2>
            <p className="mt-5 text-sm leading-7 text-foreground-700 md:text-base">
              我们定位为一个"作品整理与推荐平台"，服务对象是所有热爱火影同人创作的读者。我们的理念很简单：尊重创作、清晰呈现、持续更新。我们相信，好的作品值得被推荐，懂它的读者也终会相遇。
            </p>
          </div>

          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-primary-600">
              内容原则
            </span>
            <div className="mt-2 grid gap-4 sm:grid-cols-2">
              {values.map((v) => (
                <div
                  key={v.title}
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${v.icon} text-xl`} />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                    {v.title}
                  </h3>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600 md:text-sm">
                    {v.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100 py-14 md:py-16">
        <div className="container-x text-center">
          <span className="text-xs font-semibold uppercase tracking-wider text-primary-600">
            用户口碑
          </span>
          <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950">
            读者怎么说
          </h2>
          <div className="mx-auto mt-8 grid max-w-4xl gap-4 sm:grid-cols-3">
            {[
              { name: "追更阿青", role: "穿越爱好者", text: "分类特别清楚，穿越和系统分得明明白白，再也不用大海捞针找作品了。" },
              { name: "木叶老粉", role: "战斗党", text: "更新整理得很及时，追更进度一目了然，已经收藏好几部连载中的了。" },
              { name: "同人爱好者", role: "日常党", text: "从战斗到日常都有覆盖，介绍写得到位，评分热度也很靠谱。" },
            ].map((r) => (
              <blockquote
                key={r.name}
                className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-6 text-left"
              >
                <div className="flex items-center gap-1 text-accent-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm" />
                  ))}
                </div>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">“{r.text}”</p>
                <footer className="mt-4 border-t border-background-100 pt-3">
                  <p className="text-sm font-semibold text-foreground-900">{r.name}</p>
                  <p className="text-xs text-foreground-500">{r.role}</p>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x py-14 md:py-16">
        <div className="rounded-lg border border-background-200 bg-background-50 p-8 text-center">
          <h2 className="font-heading text-xl font-black text-foreground-950">联系与建议</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
            如果你有喜欢的火影同人作品希望被收录，或对网站有任何建议，欢迎随时与我们联系。你的每一次反馈，都能帮助我们做得更好。
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            <span className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700">
              <i className="ri-mail-line text-primary-600" />
              {site.email}
            </span>
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium whitespace-nowrap text-background-50 hover:bg-primary-600"
            >
              查看常见问题
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}